import { Box, Button, Icon, Input, Text, Tooltip } from "@showitinc/showit-wc";
import cx from "classnames";
import React, { useEffect, useRef, useState } from "react";
import SiteSettingsActions from "../../actions/SiteSettingsActions";
import { showDemoSiteChooser, showDomainSetupModal } from "../../adaptors/ModalAdaptors";
import AppConfig from "../../config/app";
import AppConstants from "../../constants/AppConstants";
import { useAccountState } from "../../hooks/query/useAccount";
import useRenderer from "../../hooks/query/useRenderer";
import { useSiteState } from "../../hooks/query/useSite";
import SiteUtils from "../../utils/SiteUtils";
import UpgradedIcon from "../../components/UpgradedIcon";
import styles from "./Toolbar.module.css";

const { UPDATE_ACTION_SET } = AppConstants;

const SiteInfo = () => {
  const site = useSiteState();
  const user = useAccountState();
  const siteId = site.get("site_design_id");
  const { canToggle, settled: rendererSettled } = useRenderer(siteId);
  const isUpgraded = rendererSettled && !canToggle;

  const [editingDesignName, setEditingDesignName] = useState(false);
  const [newDesignName, setNewDesignName] = useState(site.get("name", ""));
  const designNameRef = useRef(null);

  const designName = site.get("name", "");
  const siteDomain = SiteUtils.createFullUrl(site);
  const isDemo = AppConfig.demoMode;
  const isActive = user.get("isActive");
  const hasDomain = user.get("domain");

  useEffect(() => {
    if (editingDesignName && designNameRef.current) {
      designNameRef.current.focus();
    }
  }, [editingDesignName]);

  const _openSiteSettings = () => {
    SiteSettingsActions.openDialog();
  };

  // This is a nice to have feature, but not mvp, edit design name inline
  // Need to figure out how to edit AND save which is different than site settings modal
  const _editDesignName = () => {
    setEditingDesignName(true);
  };

  const _doneEditingDesignName = () => {
    setEditingDesignName(false);
    SiteSettingsActions.updateProperty({
      name: "site.name",
      value: newDesignName,
      origValue: designName,
      action: UPDATE_ACTION_SET,
    });
  };

  return (
    <Box
      className={styles["site-info-panel-wrapper"]}
      d="flex"
      align="flex-start"
      justify="center"
      ml="2"
    >
      {!editingDesignName && (
        <Text
          class={styles["design-name-text"]}
          onClick={isDemo ? null : _openSiteSettings}
          data-testid="site-name-toolbar-text"
        >
          {designName}
        </Text>
      )}
      {editingDesignName && (
        <Input
          value={newDesignName}
          ref={designNameRef}
          onSIInput={(e) => setNewDesignName(e.target.value)}
          onBlur={_doneEditingDesignName}
          hideLabel
        />
      )}

      {isUpgraded && (
        <Tooltip className={styles["upgraded-tooltip"]} position="bottom-right">
          <span slot="content">
            <Text>Updated for the latest version of Showit</Text>
          </span>
          <UpgradedIcon className={styles["upgraded-icon"]} />
        </Tooltip>
      )}

      {isActive && !isDemo && (
        <Button
          size="md"
          lowercase
          variant="link"
          className={cx([styles.domainButton, { [styles.domainButtonSelected]: siteDomain }])}
          onClick={siteDomain || hasDomain ? _openSiteSettings : showDomainSetupModal}
        >
          {siteDomain ? (
            <Text class={styles["domain-name-text"]}>{siteDomain}</Text>
          ) : (
            <>
              <Icon type="settings" className={styles["domain-icon"]} />
              Connect Your Domain
            </>
          )}
        </Button>
      )}
      {isDemo && (
        <Button
          id="demo-try-another-design"
          className={styles["demo-button"]}
          variant="feature"
          onClick={showDemoSiteChooser}
        >
          Try another design
        </Button>
      )}
    </Box>
  );
};

export default SiteInfo;
